<template>
  <VContainer class="px-3 pt-10 pt-lg-8 pb-4">
    <!-- Country/region selector sits on its OWN row at every breakpoint, left of
         (above) the legal row — mirrors current nuxt2. xs centers; sm+ left-aligns
         at lg via justify-lg="start" against the (non-fluid) container, so the row
         tracks the constrained content width, not the fluid footer width. -->
    <VRow v-if="showCountrySelector" no-gutters justify="center" class="hidden-sm-and-up pb-4">
      <VCol cols="auto">
        <RegionSelector />
      </VCol>
    </VRow>
    <VRow
      v-if="showCountrySelector"
      no-gutters
      justify="center"
      justify-lg="start"
      class="hidden-xs pb-4"
      data-testid="footer-country-row"
    >
      <VCol cols="auto">
        <RegionSelector />
      </VCol>
    </VRow>
    <!-- Legal row: copyright + locale legal links on their own row, same left
         alignment as the country row above. -->
    <VRow no-gutters justify="center" justify-lg="start" align="center" data-testid="footer-legal-row">
      <VCol cols="auto" class="link-col ml-0 mr-4 mr-md-8">
        <FooterButton id="copyright" legal-link disabled>
          {{ $t('footer.label.copyright', { copyrightDate: copyrightDate }) }}
        </FooterButton>
      </VCol>
      <Component
        :is="type"
        v-if="type"
        :data-testid="type.name"
      />
    </VRow>
  </VContainer>
</template>

<script lang="ts" setup>
import FooterButton from '~/components/layout/footer/FooterButton.vue'
import FooterLegalEu from '~/components/layout/footer/legal/FooterLegalEu.vue'
import FooterLegalNa from '~/components/layout/footer/legal/FooterLegalNa.vue'
import FooterLegalSp from '~/components/layout/footer/legal/FooterLegalSp.vue'
import RegionSelector from '~/components/layout/footer/RegionSelector.vue'
import { useLocaleStore } from '~/stores/locale'
import { useCartStore } from '~/stores/cart'

const localeStore = useLocaleStore()
const cartStore = useCartStore()
const { isDemo, isRecruit } = useSAuthState()

const copyrightDate = new Date().getFullYear()

// Hide the region/country selector for market-bound sessions (mirrors current
// nuxt2 FooterLegal): a demonstrator (or agent-in-demo), a shared-cart shopper,
// or a recruit in the join flow is pinned to a market, so switching region in
// the footer is meaningless/harmful. isDemo ≡ nuxt2 rootStore.isDemo
// (Demonstrator || Agent+isDemo-persistent); isRecruit ≡ nuxt2 ida.isIda.
const showCountrySelector = computed(() =>
  !isDemo.value && !cartStore.isSharedCartMode && !isRecruit.value
)

const type = computed(() => {
  if (isRecruit.value) {
    // Recruit/join: legal links are handled by the enrollment flow's own
    // treatment. Latent in nuxt4 (the ida layout renders no footer) but kept
    // for parity with nuxt2.
    return false
  } else if (localeStore.isNaCountry) {
    return FooterLegalNa
  } else if (localeStore.isEuCountry) {
    return FooterLegalEu
  } else {
    return FooterLegalSp
  }
})
</script>

<style lang="scss" scoped>
@use '~/assets/scss/settings';
@use 'sass:map';

// The copyright FooterButton has the `disabled` prop (it's static text, not a
// link), so the global grey disabled-button rule (global.scss, vuetify-overrides,
// !important) would turn it grey. nuxt2 kept it legible the same way:
// `#copyright { color: #6e6e6e !important }`. It sits in vuetify-overrides
// because an !important declaration in this component's own layer would lose.
// WCAG 1.4.3: see the matching rule in app/assets/scss/lib/typography.scss.
// AB#64470
@layer vuetify-overrides {
  #copyright {
    color: rgb(var(--v-theme-footerLegal)) !important;
  }
}

:deep(.link-col) {
  display: flex;
  justify-content: center;
  padding: 0;
}

// The locale legal children (FooterLegalEu/Na/Sp) render their links as siblings in a
// single wrap container with no pipe separators (FR-007); the layout lives here so the
// three stay identical.
:deep(.legal-links-col) {
  padding: 0;
}

:deep(.footer-legal-links) {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  column-gap: 16px;
  row-gap: 0;
  @media #{map.get(settings.$display-breakpoints, 'md-and-up')} {
    column-gap: 32px;
  }
  > * {
    white-space: nowrap;
  }
}
</style>
